import { Bus } from "baconjs"

/**
 * Execute a postMessage call to the external container of my window. If no such container exists this function does nothing.
 * @param message to be send.
 * @param targetOrigin to use (optional, defaults to '*')
 * @param eventBus (optional) eventbus to send the message to in addition to the container
 * @return true if the message was send, undefined otherwise.
 */
export function postMessage(
    message: string,
    eventBus?: Bus<unknown>,
    targetOrigin = "*"
): boolean | undefined {
    const container = window.parent
    try {
        if (container !== window.self) {
            container.postMessage(message, targetOrigin)
            return true
        }
    } finally {
        eventBus?.push(message)
    }
}

/**
 * Register a callback function to be called whenever a specified message event was received via a postMessage call to the containing window.
 * @param externalMessageID id of the external message to register for (mandatory).
 * @param callback to be called with the message event when the external message was received (mandatory).
 * @return an function reference to deregister the callback again or undefined if either of the arguments was undefined.
 */
export function registerForExternalMessage(
    externalMessageID: string,
    callback: (e: MessageEvent) => void
): (() => void) | undefined {
    if (!externalMessageID || !callback) {
        console.error(
            "eventID and callback-function are mandatory!",
            externalMessageID,
            callback
        )
        return
    }
    const type = "message"
    const eventListener = (event: MessageEvent) => {
        if (event.source !== window.self && event.data === externalMessageID) {
            callback(event)
        }
    }
    window.addEventListener(type, eventListener)
    return () => window.removeEventListener(type, eventListener)
}
